import { ExpenseCategory } from '../enums/expense-category.enum';
import { ExpenseReturnReason } from '../enums/expense-return-reason.enum';
import { ExpenseStatus } from '../enums/expense-status.enum';

/**
 * Modelo de dominio del módulo de Gastos.
 *
 * Es lo único que consumen los componentes. La forma de la API (PascalCase)
 * vive en `expense-api.model.ts` y no sale del mapper.
 */

/** Traza de la decisión de operaciones. */
export interface ExpenseReview {
  /**
   * Motivo de la devolución, del catálogo cerrado. `null` al rechazar —esa
   * decisión no tiene catálogo— y en los gastos devueltos antes de que
   * existiera, que sólo llevan el texto.
   */
  returnReasonCode: ExpenseReturnReason | null;
  /** Texto libre. Obligatorio al rechazar, opcional al devolver. */
  rejectionReason: string | null;
}

export interface Expense {
  /**
   * null en los gastos históricos. Sin él no se puede consultar el detalle,
   * editar ni cambiar el estado: esas filas son de sólo lectura.
   */
  expenseId: string | null;
  /** `B-A0DA99CA-1`. Junto a `expenseId` identifica el gasto en la API. */
  nodeId: string;
  orderHash: string;
  zoneId: string | null;
  /** null cuando el pedido del gasto no resuelve cliente. */
  clientId: string | null;
  clientName: string | null;
  driverId: string | null;
  /** null en buena parte del histórico, porque el nodo tampoco lo tiene. */
  driverName: string | null;
  licensePlate: string | null;
  category: ExpenseCategory;
  description: string | null;
  /**
   * Importe en la unidad menor de la divisa (céntimos para EUR).
   *
   * La API trabaja con decimales, pero internamente se guarda en entero para no
   * acumular error de coma flotante en dinero. La conversión vive en el mapper.
   */
  amountInCents: number;
  currencyCode: string;
  /** null en históricos: no se ha revisado nunca. */
  status: ExpenseStatus | null;
  /** null en históricos. Hora local del servicio, sin conversión de zona. */
  reportedAt: Date | null;
  receiptUrl: string | null;
  review: ExpenseReview | null;
  /**
   * `false` cuando al cliente no se le factura esta categoría. `null` mientras no se haya
   * decidido, que es lo que pasa hasta que alguien aprueba el gasto.
   */
  isRebilledToClient: boolean | null;
}

/**
 * Nombre del cliente a mostrar.
 *
 * Se resuelve el `clientId` contra el catálogo de cuentas comerciales —el mismo
 * que alimenta el filtro— y NO se usa el `clientName` del contrato sin más: ese
 * campo llega de `ClientInformation` del pedido, que modela a la persona que
 * pide el servicio (lleva su email, su teléfono y su documento), no a la cuenta.
 * Para un pedido de SIXT SAS PARIS traía "Sixt Amiens Centre Ville".
 *
 * Se cae al nombre del contrato cuando el id no está en el catálogo, que es lo
 * que pasa con los particulares: un pedido B2C no tiene cuenta de negocio y ahí
 * el nombre del contacto SÍ es el del cliente.
 */
export function clientNameOf(
  expense: Expense,
  clientNames: ReadonlyMap<string, string>
): string | null {
  const resolved = expense.clientId
    ? clientNames.get(expense.clientId)
    : undefined;

  return resolved ?? expense.clientName;
}

/** Un gasto es accionable sólo si la API puede identificarlo. */
export function isActionable(expense: Expense): boolean {
  return expense.expenseId !== null;
}

/** Campos por los que el listado permite ordenar, con el nombre del contrato. */
export type ExpenseSortField =
  | 'reportedAt'
  | 'orderHash'
  | 'driverName'
  | 'licensePlate'
  | 'category'
  | 'amount'
  | 'status';

export type SortDirection = 'asc' | 'desc';

export interface ExpenseSort {
  field: ExpenseSortField;
  direction: SortDirection;
}

/**
 * Consulta completa del listado. Filtrado, orden y paginación se resuelven en
 * servidor; el data source mock los reproduce para que el contenedor sea el
 * mismo en los dos casos.
 */
export interface ExpensesQuery {
  searchTerm: string;
  /**
   * Rango de fecha de subida, `YYYY-MM-DD`, con **los dos extremos incluidos**.
   *
   * Se trabaja con cadenas y no con `Date` a propósito: es el formato que espera
   * la API y el que da `input[type=date]`, así no hay conversiones de zona por
   * medio ni sorpresas con el día que se envía.
   */
  fromDate: string | null;
  toDate: string | null;
  /** Vacío = todas. */
  categories: ExpenseCategory[];
  /** Vacío = todos. */
  statuses: ExpenseStatus[];
  /** Zonas operativas seleccionadas. Vacío = todas. */
  zoneIds: string[];
  /** Clientes seleccionados. Vacío = todos. */
  clientIds: string[];
  /** 1-based. */
  page: number;
  pageSize: number;
  sort: ExpenseSort;
}

export interface ExpensesPage {
  items: Expense[];
  totalCount: number;
}

/** Referencia a un gasto concreto en la API. */
export interface ExpenseRef {
  nodeId: string;
  expenseId: string;
}

export interface CreateExpensePayload {
  /** `B-A0DA99CA-1`: el servicio al que se imputa el gasto. */
  nodeId: string;
  driverId: string;
  driverName: string | null;
  licensePlate: string;
  category: ExpenseCategory;
  description: string;
  amountInCents: number;
  status: ExpenseStatus;
  /** Obligatorio al rechazar; al devolver sólo amplía al código. */
  reason: string | null;
  /** Motivo del catálogo. Sólo al devolver. */
  reasonCode: ExpenseReturnReason | null;
  receiptFile: File | null;
}

export interface UpdateExpensePayload {
  category: ExpenseCategory;
  description: string;
  amountInCents: number;
  status: ExpenseStatus;
  /** Obligatorio al rechazar; al devolver sólo amplía al código. */
  reason: string | null;
  /** Motivo del catálogo. Sólo al devolver. */
  reasonCode: ExpenseReturnReason | null;
  /** Si se envía, sustituye al comprobante anterior. */
  receiptFile: File | null;
  /**
   * Guardar el gasto **sin** comprobante.
   *
   * Existe porque un multipart sin fichero no distingue «no he tocado la foto»
   * —lo habitual— de «he quitado la foto».
   */
  removeReceipt: boolean;
}

export interface ExpenseStatusChangePayload {
  status: ExpenseStatus;
  reason: string | null;
  /** Sólo al devolver: el contrato rechaza un código en cualquier otro estado. */
  reasonCode: ExpenseReturnReason | null;
}

/** Opción de conductor para el buscador del formulario de alta. */
export interface ExpenseDriverOption {
  driverId: string;
  fullName: string;
}
